import { useState, useEffect } from 'react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { useSession } from '@/lib/session/SessionContext'

const PLAN_LABELS: Record<string, string> = {
  sandbox: 'Sandbox',
  researcher: 'Researcher',
  agency_pro: 'Agency Pro',
  agency_studio: 'Agency Studio',
  enterprise: 'Enterprise',
}

export function SettingsPage() {
  const { session } = useSession()
  const [plan, setPlan] = useState<string | null>(null)
  const [dataResidency, setDataResidency] = useState<string>('US')

  useEffect(() => {
    const token = localStorage.getItem('echo_session')
    if (!token) return
    fetch('/api/me', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() as Promise<{ plan?: string; data_residency?: string }> : null)
      .then(d => {
        if (d?.plan) setPlan(d.plan)
        if (d?.data_residency) setDataResidency(d.data_residency)
      })
      .catch(() => {})
  }, [])

  return (
    <div className="p-6 space-y-8 max-w-3xl pb-20 md:pb-6">
      <h1 className="text-2xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>Settings</h1>

      {/* Account */}
      <section className="space-y-4">
        <h2 className="text-base font-medium text-deep-ink border-b border-border-gray pb-2">Account</h2>
        <Card className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-500 block mb-1">Email</label>
              <p className="text-sm text-deep-ink">{session?.email ?? '—'}</p>
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Workspace</label>
              <p className="text-sm text-deep-ink">{session?.is_demo ? 'Demo workspace' : session?.tenant_id ?? '—'}</p>
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Plan</label>
              {plan
                ? <Badge variant={plan === 'sandbox' ? 'demo' : 'success'}>{PLAN_LABELS[plan] ?? plan}</Badge>
                : <span className="text-sm text-slate-400">—</span>
              }
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Data residency</label>
              <p className="text-sm text-deep-ink">{dataResidency}</p>
            </div>
          </div>
        </Card>
      </section>

      {/* API Keys */}
      <section className="space-y-4">
        <h2 className="text-base font-medium text-deep-ink border-b border-border-gray pb-2">API Keys</h2>
        <Card className="space-y-4">
          <p className="text-sm text-slate-600">API keys allow external plugins and server-to-server integrations to access the Echosome API on behalf of this workspace.</p>
          <div className="bg-soft-panel rounded-lg p-4 border border-border-gray">
            <p className="text-xs text-slate-500 text-center">No API keys yet.</p>
          </div>
          <Button variant="outline" size="sm" disabled>Generate API key (upgrade required)</Button>
        </Card>
      </section>

      {/* Webhooks */}
      <section className="space-y-4">
        <h2 className="text-base font-medium text-deep-ink border-b border-border-gray pb-2">Webhooks</h2>
        <Card className="space-y-4">
          <p className="text-sm text-slate-600">Configure webhook destinations to receive events when pre-reads complete, validations finish, or panels update.</p>
          <div className="space-y-2">
            {['pre_read.completed', 'validation.completed', 'panel.updated'].map(event => (
              <div key={event} className="flex items-center justify-between py-2 border-b border-border-gray last:border-0">
                <span className="text-sm font-mono text-slate-600">{event}</span>
                <Badge variant="outline" size="sm">Not configured</Badge>
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" disabled>Add webhook (upgrade required)</Button>
        </Card>
      </section>

      {/* Security activity */}
      <section className="space-y-4">
        <h2 className="text-base font-medium text-deep-ink border-b border-border-gray pb-2">Security & Activity</h2>
        <Card className="space-y-3">
          {[
            { action: 'session.created', time: 'Just now', resource: session?.email ?? '—' },
            { action: 'workspace.loaded', time: '1 min ago', resource: session?.tenant_id ?? 'workspace' },
          ].map((entry, i) => (
            <div key={i} className="flex items-start justify-between gap-4 text-sm py-2 border-b border-border-gray last:border-0">
              <div>
                <span className="font-mono text-xs text-slate-600">{entry.action}</span>
                <p className="text-xs text-slate-400 mt-0.5">{entry.resource}</p>
              </div>
              <span className="text-xs text-slate-400 shrink-0">{entry.time}</span>
            </div>
          ))}
        </Card>
      </section>

      {/* White-label */}
      <section className="space-y-4">
        <h2 className="text-base font-medium text-deep-ink border-b border-border-gray pb-2">White-label</h2>
        <Card className="space-y-3">
          <p className="text-sm text-slate-600">Upload your agency logo, set primary color, and customize report footer text. Available on Agency Studio and above.</p>
          <Button variant="outline" size="sm" disabled>Configure white-label (Agency Studio+)</Button>
        </Card>
      </section>
    </div>
  )
}
